import {StyleSheet} from "aphrodite";
import {useState, type ReactNode} from "react";
import {Editor, Transforms, type Element} from "slate";
import {ReactEditor, useSlate} from "slate-react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {border, color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {isKhanmigoEditing} from "@khan/guide-math-editor";

import {isListItemNode} from "./slate/helpers/list-helpers.ts";
import {isSelectionWithinNode} from "./slate/helpers/node-helpers.ts";

interface Props {
    element: Element;
    children: ReactNode;
}
const GuideColors = {
    darkPurple: color.eggplant,
    lightPurple: "#DCBBDA",
    peach: "#F7F3FF",
    pink: "#F5CFFF",
} as const;

const StyledSpan = addStyle("span");

export default function GutterDecorator(props: Props) {
    const {element, children} = props;

    const editor = useSlate();
    const [isHovered, setIsHovered] = useState(false);

    if (isKhanmigoEditing(editor)) {
        // If Khanmigo is typing, we don't show any gutter decorators.
        return children;
    }

    const isFocused = isSelectionWithinNode(editor, element);
    const isOnListItem = isListItemNode(element);

    const handleClick = () => {
        // When the decorator is clicked, we select the whole element
        // that this decorator surrounds:
        const path = ReactEditor.findPath(editor, element);
        const range = Editor.range(editor, path);
        Transforms.select(editor, range);
    };

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
        <StyledSpan
            style={styles.container}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error */}
            <StyledSpan
                data-remove-on-print
                contentEditable={false}
                onClick={handleClick}
                style={[
                    styles.decorator,
                    isHovered && styles.hovered,
                    isFocused && styles.focused,
                    isOnListItem && styles.forListItem,
                ]}
            />
            {children}
        </StyledSpan>
    );
}

const styles = StyleSheet.create({
    container: {
        display: "block",
        position: "relative",
    },
    decorator: {
        display: "block",
        position: "absolute",
        top: -spacing.xxxxSmall_2,
        height: "100%",
        borderRadius: border.radius.radius_040,
        insetInlineStart: -spacing.large_24,
        width: spacing.small_12,
        cursor: "pointer",
        transition: "background 0.2s ease-in-out",
    },
    hovered: {
        background: GuideColors.lightPurple,
    },
    focused: {
        background: GuideColors.darkPurple,
    },
    forListItem: {
        // List items have an extra marker to their left already (e.g.
        // the bullet point), so we shift the gutter decorator a little
        // further to not overlap with it:
        insetInlineStart: -spacing.xxLarge_48,
    },
});
